import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { RefreshCw, Lock, Unlock, ShieldAlert } from 'lucide-react';
import RequestBuilderPanel from './RequestBuilderPanel';
import SessionLogPanel from './SessionLogPanel';
import ReferenceDrawer from './ReferenceDrawer';
import AuthorProfileEditor from './AuthorProfileEditor';
import ScenarioGuide from './ScenarioGuide';
import { createDefaultProfile } from '../../../lib/uds/profile';
import { createInitialEcuState, processRequest, tickEcu, maybeInjectFault } from '../../../lib/uds/engine';
import { decodeRequest, decodeResponse, extractRequestMeta, extractResponseMeta } from '../../../lib/uds/decoder';
import { SESSION_NAME_BY_KEY, bytesToHexString } from '../../../lib/uds/constants';

const INPUT_MODE_KEY = 'uds-simulator-input-mode';
let idCounter = 0;
const nextId = () => { idCounter += 1; return idCounter; };

const UdsDiagnosticSimulator = () => {
  const [mode, setMode] = useState('learner');
  const [inputMode, setInputModeState] = useState(() => localStorage.getItem(INPUT_MODE_KEY) || 'guided');
  const [profile, setProfile] = useState(createDefaultProfile);
  const [ecuState, setEcuState] = useState(() => createInitialEcuState(profile));
  const [log, setLog] = useState([]);
  const [faultInjection, setFaultInjection] = useState(false);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [lastSeed, setLastSeed] = useState(null);
  const sessionStartRef = useRef(Date.now());

  const setInputMode = useCallback((v) => {
    setInputModeState(v);
    localStorage.setItem(INPUT_MODE_KEY, v);
  }, []);

  const tSeconds = () => (Date.now() - sessionStartRef.current) / 1000;

  const pushSystemEntry = useCallback((message) => {
    setLog((prev) => [...prev, { id: nextId(), direction: 'system', tSeconds: tSeconds(), summary: message }]);
  }, []);

  // Time-based ECU behavior (S3 session timeout, lockout expiry) — runs even
  // without a new tester request, and injects occasional realistic faults when
  // Practitioner mode has fault injection enabled.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setEcuState((prev) => {
        let state = prev;
        const tick = tickEcu(profile, state, now);
        if (tick.event) {
          state = tick.nextState;
          pushSystemEntry(tick.event.message);
        }
        if (mode === 'practitioner' && faultInjection) {
          const fault = maybeInjectFault(profile, state, now);
          if (fault.event) {
            state = fault.nextState;
            pushSystemEntry(fault.event.message);
          }
        }
        return state;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [profile, mode, faultInjection, pushSystemEntry]);

  const handleSend = (bytes) => {
    if (!bytes) return;
    const now = Date.now();
    const requestMeta = extractRequestMeta(bytes);
    const requestDecoded = decodeRequest(bytes, profile);
    setLog((prev) => [...prev, {
      id: nextId(),
      direction: 'request',
      tSeconds: tSeconds(),
      bytesHex: bytesToHexString(bytes),
      summary: requestDecoded.summary,
      breakdown: requestDecoded.breakdown,
    }]);

    const { nextState, responseBytes } = processRequest(profile, ecuState, bytes, { now });
    setEcuState(nextState);

    if (requestMeta.sid === 0x27 && requestMeta.subFn === 0x01 && responseBytes && responseBytes[0] === 0x67) {
      setLastSeed(responseBytes.slice(2, 4));
    }

    if (responseBytes === null) {
      pushSystemEntry('Positive response suppressed by tester (suppressPosRspMsgIndicationBit set).');
      return;
    }
    const responseMeta = extractResponseMeta(responseBytes, requestMeta);
    const responseDecoded = decodeResponse(responseBytes, bytes, profile);
    setLog((prev) => [...prev, {
      id: nextId(),
      direction: 'response',
      tSeconds: tSeconds(),
      bytesHex: bytesToHexString(responseBytes),
      summary: responseDecoded.summary,
      positive: responseDecoded.positive,
      breakdown: responseDecoded.breakdown,
      meta: responseMeta,
    }]);
  };

  const handleApplyProfile = (newProfile) => {
    setProfile(newProfile);
    setEcuState(createInitialEcuState(newProfile));
    setLastSeed(null);
    setLog([{ id: nextId(), direction: 'system', tSeconds: tSeconds(), summary: `Profile "${newProfile.name}" applied — simulator state reset.` }]);
  };

  const handleResetSession = () => {
    setEcuState(createInitialEcuState(profile));
    setLastSeed(null);
    setLog([{ id: nextId(), direction: 'system', tSeconds: tSeconds(), summary: 'ECU power-cycled — session, security, and DID edits reset. DTC memory retained.' }]);
  };

  const handleClearLog = () => setLog([]);

  const handleExportLog = () => {
    const lines = log.map((e) => `+${e.tSeconds.toFixed(2)}s [${e.direction.toUpperCase()}] ${e.bytesHex || ''} ${e.summary}`);
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `uds-session-log-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const lockoutActive = ecuState.lockoutUntil && Date.now() < ecuState.lockoutUntil;

  return (
    <div className="space-y-6" data-testid="uds-simulator">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList>
            <TabsTrigger value="learner" data-testid="uds-tab-learner">Learner</TabsTrigger>
            <TabsTrigger value="practitioner" data-testid="uds-tab-practitioner">Practitioner</TabsTrigger>
            <TabsTrigger value="author" data-testid="uds-tab-author">Author</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="font-mono" data-testid="uds-session-badge">
            Session: {SESSION_NAME_BY_KEY[ecuState.session]}
          </Badge>
          <Badge variant="outline" className="font-mono flex items-center gap-1" data-testid="uds-security-badge">
            {ecuState.securityUnlocked ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
            Security: {lockoutActive ? 'Locked Out' : ecuState.securityUnlocked ? 'Unlocked' : 'Locked'}
          </Badge>
          {mode === 'practitioner' && (
            <div className="flex items-center gap-2 pl-2">
              <Switch checked={faultInjection} onCheckedChange={setFaultInjection} id="fault-injection" data-testid="uds-fault-injection-switch" />
              <Label htmlFor="fault-injection" className="text-xs flex items-center gap-1"><ShieldAlert className="h-3.5 w-3.5" /> Fault Injection</Label>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={handleResetSession} data-testid="uds-reset-session-btn">
            <RefreshCw className="h-3.5 w-3.5" /> Reset ECU
          </Button>
          <ReferenceDrawer profile={profile} />
        </div>
      </div>

      {mode === 'learner' && (
        <ScenarioGuide activeScenarioId={activeScenarioId} setActiveScenarioId={setActiveScenarioId} log={log} ecuState={ecuState} />
      )}

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <RequestBuilderPanel
          mode={mode}
          inputMode={inputMode}
          setInputMode={setInputMode}
          profile={profile}
          ecuState={ecuState}
          onSend={handleSend}
          lastSeed={lastSeed}
        />
        <SessionLogPanel log={log} onClear={handleClearLog} />
      </div>

      {mode === 'author' && (
        <AuthorProfileEditor profile={profile} onApply={handleApplyProfile} onExportLog={handleExportLog} />
      )}
    </div>
  );
};

export default UdsDiagnosticSimulator;
